// Emoji conversion rules shared with Android: behaviour lives in convertCases.json.
// Indexes (caret, start, end) are UTF-16 code unit offsets; limits count code points.

export type EmojiUndo = { start: number; end: number; original: string }
export type EmojiEdit = { value: string; caret: number; undo: EmojiUndo | null }

type EmojiList = { shortcodes: Record<string, string>; emoticons: Record<string, string> }

let list: EmojiList | null = null

/** Loads the emoji list (separate chunk). Nothing converts until this has resolved. */
export async function ensureEmojiList(): Promise<void> {
  if (list) return
  const mod = await import('./emojiShortcuts.json')
  list = mod.default as EmojiList
}

const own = (o: Record<string, string>, k: string): string | undefined =>
  Object.prototype.hasOwnProperty.call(o, k) ? o[k] : undefined

const isSpace = (ch: string) => /^\s$/u.test(ch)
const cpLength = (s: string) => [...s].length

function looksLikeAddress(word: string): boolean {
  return (
    /[a-z][a-z0-9+.-]*:\/\//i.test(word) ||
    /(^|[^\p{L}\p{N}])www\./iu.test(word) ||
    /\S@\S+\.\S/.test(word) ||
    // A bare domain before a path (example.com/, example.com:8080/), or IPv6 (fe80::1).
    /[\p{L}\p{N}]\.\p{L}{2,}(:[0-9]+)?\//u.test(word) ||
    word.includes('::')
  )
}

/** The whitespace-delimited word around the caret. */
export function wordAt(text: string, caret: number): string {
  let s = caret
  while (s > 0 && !isSpace(text[s - 1])) s--
  let e = caret
  while (e < text.length && !isSpace(text[e])) e++
  return text.slice(s, e)
}

function overLimit(value: string, max: number | undefined): boolean {
  return max !== undefined && cpLength(value) > max
}

export function convertTyped(
  prev: string,
  next: string,
  caret: number,
  opts: { max?: number; composing?: boolean; suppress?: string | null },
): EmojiEdit | null {
  if (!list || opts.composing) return null
  if (caret < 1 || caret > next.length) return null
  // Only one code point typed right before the caret can convert (this excludes paste).
  const cp = [...next.slice(0, caret)].pop()!
  const at = caret - cp.length
  if (next.slice(0, at) + next.slice(caret) !== prev) return null

  let start: number
  let end: number
  let emoji: string
  let original: string
  let wordForCheck: string
  let matched: string

  if (cp === ':') {
    const m = /:([^\s:]+):$/u.exec(next.slice(0, caret))
    if (!m) return null
    // Not inside a word or number: x:heart:, 12:100:
    const before = [...next.slice(0, m.index)].pop()
    if (before !== undefined && /[\p{L}\p{N}]/u.test(before)) return null
    const found = own(list.shortcodes, m[1])
    if (found === undefined) return null
    start = m.index
    end = caret
    emoji = found
    original = m[0]
    wordForCheck = wordAt(next, caret)
    matched = m[0]
  } else if (isSpace(cp)) {
    const word = /\S+$/u.exec(next.slice(0, at))?.[0]
    if (!word) return null
    const found = own(list.emoticons, word)
    if (found === undefined) return null
    start = at - word.length
    end = caret
    emoji = found + cp
    original = word + cp
    wordForCheck = word
    matched = word
  } else {
    return null
  }

  if (looksLikeAddress(wordForCheck) || (opts.suppress && matched === opts.suppress)) return null
  const value = next.slice(0, start) + emoji + next.slice(end)
  if (overLimit(value, opts.max)) return null
  const newEnd = start + emoji.length
  return { value, caret: newEnd, undo: { start, end: newEnd, original } }
}

/**
 * Backspace right after a conversion: `prev` is the converted text, `next` the text after the
 * deletion. Brings back the original; null when the deletion is anywhere else.
 */
export function restoreOnBackspace(
  prev: string,
  next: string,
  caret: number,
  undo: EmojiUndo,
): EmojiEdit | null {
  const removed = prev.length - next.length
  if (removed <= 0 || caret < undo.start || caret + removed !== undo.end) return null
  if (prev.slice(0, caret) + prev.slice(caret + removed) !== next) return null
  const deleted = prev.slice(caret, caret + removed)
  const typedSpace = /^\s+$/u.test(deleted) && /\s$/u.test(undo.original)
  const restored = typedSpace ? undo.original.slice(0, undo.original.length - removed) : undo.original
  const value = prev.slice(0, undo.start) + restored + prev.slice(undo.end)
  return { value, caret: undo.start + restored.length, undo: null }
}

/**
 * For Send: converts an emoticon that is the last word of the text, unless it is the word
 * `suppress` (just restored by Backspace, `undo.original.trimEnd()`).
 */
export function convertAtEnd(text: string, max?: number, suppress?: string | null): string {
  if (!list) return text
  const word = /\S+$/u.exec(text)?.[0]
  if (!word || looksLikeAddress(word) || word === suppress) return text
  const found = own(list.emoticons, word)
  if (found === undefined) return text
  const value = text.slice(0, text.length - word.length) + found
  return overLimit(value, max) ? text : value
}
